import { Button } from '@app/components/various/Button';
import { EmptyState } from '@app/components/various/EmptyState';
import { Link } from '@app/components/various/Link';
import { PageTitle } from '@app/components/various/PageTitle';
import { Pagination } from '@app/components/various/Pagination';
import { SearchTopBar } from '@app/components/various/SearchTopBar';
import { CollapsibleListCell, ExpanderCell, Table } from '@app/components/various/Table';
import { getUser } from '@app/ducks';
import { getIsAccountEditingEnabled } from '@app/ducks/config';
import { getHasPermissions } from '@app/logic/User';
import { compiledPaths } from '@app/paths';
import { useAccounts } from '@app/services/hooks/accounts/useAccounts';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { cn } from '@app/utils/cn';
import { usePageScrolling, useUrlPagination, useUrlState } from '@app/utils/hooks';
import { usePaginationHandler } from '@app/utils/hooks/table/usePaginationHandler';
import { useTable } from '@app/utils/hooks/table/useTable';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { renderMaskedCell } from '@app/utils/table';
import { createColumnHelper, ExpandedStateList, getCoreRowModel, Row } from '@tanstack/react-table';
import { Account, Buyer } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as ReactLink } from 'react-router-dom';

import { CreateAccountModal } from '../CreateAccount/CreateAccountModal';

import { AccountListCard } from './AccountListCard';
import { AccountListCardSkeleton } from './AccountListCardSkeleton';
import { AccountListSubRow } from './AccountListSubRow';

interface AccountListing extends Omit<Account, 'buyers'> {
  buyers: Buyer.Base[];
}

const columnHelper = createColumnHelper<AccountListing>();

export const AccountList = () => {
  const { t } = useTranslation(['common', 'accounts', 'buyers']);
  const { scrollToTopDelay } = usePageScrolling();
  const { trackEvent } = useAmplitude();
  const user = useSelector(getUser);
  const isAccountEditingEnabled = useSelector(getIsAccountEditingEnabled);
  const hasUserCreateAccountPermissions = getHasPermissions(user, 'CREATE_ACCOUNT');
  const { showModal: showCreateAccountModal, isOpen: showCreateAccountModalOpen, hideModal: hideCreateAccountModal } = useModalToggle();

  const [page, setPage] = useUrlPagination();
  const [searchQuery, setSearchQuery] = useUrlState('search');
  const [expanded, setExpanded] = React.useState<ExpandedStateList>({});

  const { data: accountsData, isLoading: isLoadingInitialQuery, isPlaceholderData, isError } = useAccounts({ page, search: searchQuery });

  const accounts = React.useMemo(() => {
    return Object.values(accountsData?.data ?? {}).map(account => ({ ...account, buyers: Object.values(account.buyers) }));
  }, [accountsData]);

  const accountsTotal = accountsData?.total ?? 0;
  const isPending = isLoadingInitialQuery || isPlaceholderData;

  const handleRowClick = React.useCallback(
    (row: Row<AccountListing>) => {
      const isRowExpanded = expanded[row.id] ?? false;
      setExpanded(expandedState => {
        return { ...expandedState, [row.id]: !isRowExpanded };
      });
    },
    [setExpanded, expanded],
  );

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('account', {
        cell: renderMaskedCell,
        header: t('common:id'),
        meta: {
          align: 'center',
          width: 60,
        },
      }),
      columnHelper.accessor('name', {
        cell: ({ row }) => (
          <ReactLink
            data-sentry-mask
            className="table-row-interaction rounded-small body-1 focus-visible:focus-outline"
            to={compiledPaths.ACCOUNT_DETAILS({ accountId: row.original.account })}
            onClick={() => trackEvent({ name: 'accountsTable.link.row.click' })}
          >
            {row.original.name}
          </ReactLink>
        ),
        header: t('common:name'),
      }),
      columnHelper.accessor('email', {
        cell: ({ row }) => {
          const { email } = row.original;

          if (!isDefined(email) || isEmpty(email)) {
            return null;
          }

          return (
            <div className="flex whitespace-pre">
              {email.split(',').map((singleEmail, index) => (
                <React.Fragment key={index}>
                  {index !== 0 && ', '}
                  <Link key={singleEmail} target="_blank" to={`mailto:${singleEmail}`} data-sentry-mask>
                    {singleEmail}
                  </Link>
                </React.Fragment>
              ))}
            </div>
          );
        },
        header: t('common:email'),
      }),
      columnHelper.accessor('buyers', {
        cell: ({ row }) => {
          const { buyers } = row.original;
          const isExpanded = row.getIsExpanded();

          const buyerNames = buyers
            .map(buyer => ({
              key: buyer.buyer,
              name: `${buyer.firstName} ${buyer.lastName}`.trim(),
            }))
            .filter(buyer => !isEmpty(buyer.name));

          return (
            <ReactLink
              to={compiledPaths.ACCOUNT_DETAILS({ accountId: row.original.account })}
              tabIndex={-1}
              className={cn('contents', { invisible: isExpanded })}
              onClick={() => trackEvent({ name: 'accountsTable.link.row.click' })}
            >
              <CollapsibleListCell
                items={buyerNames.map(({ name, key }) => (
                  <span data-sentry-mask key={key} className={`after:whitespace-pre [&:not(:last-child)]:after:content-[",_"]`}>
                    {name}
                  </span>
                ))}
              />
            </ReactLink>
          );
        },
        header: t('buyers:buyer_other'),
        meta: {
          isCollapsible: true,
          width: '60%',
        },
      }),
      columnHelper.display({
        cell: ({ row }) => (
          <ExpanderCell
            rowId={row.id}
            onExpand={() => {
              handleRowClick(row);
              trackEvent({ name: 'accountsTable.button.expander.click' });
            }}
            expanded={row.getIsExpanded()}
          />
        ),
        id: 'options',
        meta: {
          align: 'center',
        },
      }),
    ],
    [handleRowClick, t, trackEvent],
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    scrollToTopDelay();
  };

  const { pagination, handlePaginationChange } = usePaginationHandler({ onPageChange: handlePageChange, page });

  const handleSearchChange = (inputValue: string) => {
    setSearchQuery(inputValue, { resetParams: ['page'] });
    scrollToTopDelay();
  };

  const hasAccounts = accounts.length > 0;

  const paginationInfo = React.useMemo(
    () => ({
      current: page,
      total: accountsTotal,
    }),
    [page, accountsTotal],
  );

  const shouldShowTable = isPending || isError || hasAccounts;

  const table = useTable({
    columns,
    data: accounts,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand: () => true,
    manualPagination: true,
    onExpandedChange: setExpanded,
    onPaginationChange: handlePaginationChange,
    state: {
      expanded,
      pagination,
    },
  });

  return (
    <PageTitle title={t('accounts:account_other')}>
      <div className="px-4 lg:px-8">
        <SearchTopBar
          searchPlaceholder={t('accounts:search_account')}
          title={t('accounts:account_other')}
          totalCount={accountsData?.total}
          postfix={
            isAccountEditingEnabled &&
            hasUserCreateAccountPermissions && (
              <Button onClick={showCreateAccountModal} dataTestId="createSelectionButton">
                {t('accounts:create_account')}
              </Button>
            )
          }
          search={searchQuery}
          onSearchChange={handleSearchChange}
        />
        {shouldShowTable ?
          <>
            <Table
              table={table}
              className="!grid-cols-1"
              isLoading={isPending}
              renderSubComponent={row => <AccountListSubRow row={row} />}
              renderSkeletonCard={index => <AccountListCardSkeleton key={index} />}
              renderCard={(row, index) => <AccountListCard key={index} row={row} />}
            />
            <Pagination
              onChange={table.setPageIndex}
              current={paginationInfo.current}
              totalItems={paginationInfo.total}
              onActionButtonClick={showCreateAccountModal}
              actionButtonLabel={t('accounts:create_account')}
              isHidden={isLoadingInitialQuery}
            />
          </>
        : <EmptyState title={t('accounts:no_results.search.title', { searchQuery })} subtitle={t('accounts:no_results.search.subtitle')}>
            <Button color="secondary" variant="bordered" onClick={() => setSearchQuery('')}>
              {t('common:clear_search')}
            </Button>
          </EmptyState>
        }
        {isAccountEditingEnabled && hasUserCreateAccountPermissions && (
          <CreateAccountModal
            onSuccess={hideCreateAccountModal}
            isOpen={showCreateAccountModalOpen}
            onClose={hideCreateAccountModal}
            createSource="listing"
          />
        )}
      </div>
    </PageTitle>
  );
};
